import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  useCurrentShopAdmin,
  useUpdateShopAdmin,
} from "@/api/shops/use-shop-admin";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const ShopAdminSettingsFormSchema = z.object({
  demo: z.boolean(),
  pushReasonExternalIdToShopifyNote: z.boolean(),
});

type ShopAdminSettingsFormValues = z.infer<typeof ShopAdminSettingsFormSchema>;

function ShopAdminSettingsForm({
  demo,
  pushReasonExternalIdToShopifyNote,
}: ShopAdminSettingsFormValues) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShopAdmin } = useUpdateShopAdmin(shop.id);

  const form = useForm<ShopAdminSettingsFormValues>({
    mode: "onChange",
    defaultValues: { demo, pushReasonExternalIdToShopifyNote },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit((next: ShopAdminSettingsFormValues) =>
    updateShopAdmin({
      returnWidget: { demo: next.demo },
      pushReasonExternalIdToShopifyNote:
        next.pushReasonExternalIdToShopifyNote,
    }).then(() => form.reset(next)),
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  return (
    <Form {...form}>
      <form>
        <FormField
          control={form.control}
          name="demo"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.shop.admin.returnWidget.demo.title")}
              description={t(
                "settings.shop.admin.returnWidget.demo.description",
              )}
              {...field}
            />
          )}
        />
        <FormField
          control={form.control}
          name="pushReasonExternalIdToShopifyNote"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.shop.admin.pushReasonExternalIdToShopifyNote.title",
              )}
              description={t(
                "settings.shop.admin.pushReasonExternalIdToShopifyNote.description",
              )}
              {...field}
            />
          )}
        />
      </form>
    </Form>
  );
}

/** Baback-internal settings. Render only behind `useViewerIsSuperAdmin()`. */
export default function ShopAdminSettingsCard() {
  const { data, isPending } = useCurrentShopAdmin();

  return (
    <Card className="w-full">
      <CardContent className="py-8 px-8">
        {isPending ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <ShopAdminSettingsForm
            demo={data?.returnWidget?.demo ?? false}
            pushReasonExternalIdToShopifyNote={
              data?.pushReasonExternalIdToShopifyNote ?? false
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
